"use client";

import * as React from "react";
import { CircleCheck as CheckCircle2, Clock, FileCheck2, Loader as Loader2, Send, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const keyPoints = [{
  title: "Direct Specialist Access",
  description: "Engage with application scientists and qualification engineers across Ireland & the UK."
}, {
  title: "Rapid Technical Triage",
  description: "Initial application assessment and feasibility scoping within 24 hours."
}, {
  title: "Regulatory & Compliance Focus",
  description: "Full alignment with GMP, FDA 21 CFR Part 11, and pharmacopeial standards."
}];
function InquiryFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/50 rounded-lg border border-border">
        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Your message..." className="min-h-28 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2" size="lg">
          {isSubmitting ? <>
              <Loader2 className="animate-spin size-4" />
              <span>Submitting Inquiry...</span>
            </> : <>
              <Send className="size-4" />
              <span>Submit Inquiry</span>
            </>}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-1">
        We respect your privacy. Technical details are protected under standard NDA protocols.
      </p>
    </Form>;
}
export default function Inquiry() {
  return <section id="specialistinquirycta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Context Column (5 cols) */}
          <div className="lg:col-span-5 flex flex-col justify-between space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
                <Sparkles className="size-3.5 text-primary" />
                <span>Application & Technical Support</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                Discuss Your Application with an Instrumentation Specialist
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Connect directly with our team to evaluate analytical instruments, plan PAT integration, or request IQ/OQ qualification protocols tailored to regulated laboratory and manufacturing workflows.
              </p>
            </div>

            <div className="space-y-4">
              {keyPoints.map((point, index) => <div key={point.title} data-index={index} className="flex items-start gap-3 p-3.5 rounded-lg bg-secondary/50 border border-border/60">
                  <div className="p-1.5 rounded-md bg-background text-primary shrink-0 border border-border">
                    {index === 0 && <ShieldCheck className="size-4" />}
                    {index === 1 && <Clock className="size-4" />}
                    {index === 2 && <FileCheck2 className="size-4" />}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {point.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                      {point.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button variant="outline" asChild size="default" className="w-full sm:w-auto">
                <Link to="/BookADemoPage">Book a Demo</Link>
              </Button>
              <Button variant="secondary" asChild size="default" className="w-full sm:w-auto">
                <Link to="/QualificationLifecycleServicesPage">Lifecycle Services</Link>
              </Button>
            </div>
          </div>

          {/* Right Form Column (7 cols) */}
          <div className="lg:col-span-7">
            <Card className="border-border shadow-md bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold text-card-foreground">
                  Contact Us - Antech Solutions
                </CardTitle>
                <CardDescription>
                  Complete the form below to outline your analytical instrumentation or qualification requirements.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="contact_form" sectionName="specialist-inquiry-cta" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry. An Antech technical specialist will review your application requirements and respond promptly." errorMessage="Failed to submit your inquiry. Please verify your details and try again.">
                  <InquiryFormFields />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}